import React, { useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAppCtx } from './app-ctx';
import Pager from './pager';
import Stars from './Stars';
import safeStringify from './utils/safe-stringify';
import ResortPrices from './resort-prices';
//import {PriceCtxProvider} from './resort-prices';


const ListResorts = ({searchCriterias}) => {
    const { t } = useTranslation();

    const {JOOMLA_VARS, CONFIG} = useAppCtx();
    const confItemsPerPage = 12;
    const confVisiblePages = 5;

    const [resorts, setResorts] = React.useState([]);
    const [currentPage, setCurrentPage] = React.useState(0);
    const [pager, setPager] = React.useState({
        total: 1,
        current:currentPage,
        visiblePages: confVisiblePages,
        itemsPerPage: confItemsPerPage
    });
    const [loading, setLoading] = React.useState(false);
    const [firstLoad, setFirstLoad] = React.useState(false);
    const [errorHtml, setErrorHtml] = React.useState(null);

    const resortPriceCssClasses = {
        className: null,
        labelClassName: null,
        currencySymbolClassName: 'resort-list-item-price-currency-symbol',
        valueClassName: 'resort-list-item-price-value',
        currencyCodeClassName: 'resort-list-item-price-currency-code'
    };

    /**
     * Normalize search criterias which may come in two shapes:
     *  - from SearchForm (client state): { occupacyValue, dietaryValue, dateRangeValue }
     *  - from server-side cookie (direct navigation to /resorts): { occupacy, dietary, dates }
     * Fall back to sane defaults so that price requests always carry valid occupancy.
     */
    const occupacyValue = searchCriterias?.occupacyValue
        ?? searchCriterias?.occupacy
        ?? { adults: 2, children: 1 };
    const dietaryValue = searchCriterias?.dietaryValue ?? searchCriterias?.dietary;
    const dateRangeValue = searchCriterias?.dateRangeValue ?? searchCriterias?.dates;

    const notify = (msg) => toast(msg);

    React.useEffect(() => {
        //let vars = Joomla.getOptions('com_kurortlife.uri');
        const appRoot = JOOMLA_VARS.root;
        const csrfToken = JOOMLA_VARS.csrfToken || '';
        const apiPath = CONFIG.lookupResorts;

        // Abort the request when the effect re-runs (page changed / criterias
        // changed) or the component unmounts, so stale responses never overwrite
        // the state of a newer request (pager race condition).
        const controller = new AbortController();

        console.log('ListResorts.useEffect(): searchCriterias', JSON.stringify(searchCriterias));

        setErrorHtml(null);
        setLoading(true);

        fetch(appRoot + apiPath, {
            method: 'POST',
            headers: {'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken},
            body: JSON.stringify({
                search: searchCriterias,
                pageToLoad: currentPage,
                itemsPerPage: pager.itemsPerPage,
                visiblePages: pager.visiblePages
            }),
            signal: controller.signal
        })
        .then(response => response.text())
        /*
        {
            resorts: [{...},{...},...];
            itemsPerPage : <int>
            totalItems   : <int>
            totalPages   : <int>
            currentPage  : <int>
            visiblePages : <int>
        }
         */
        .then(text => {
            let data;
            try {
                data = JSON.parse(text);
            } catch (e) {
                console.error("ListResorts: Failed to parse JSON", e);
                setErrorHtml(text);
                return;
            }
            setResorts(data.resorts);
            setPager({
                total:        data.totalPages,
                current:      data.currentPage,
                visiblePages: data.visiblePages,
                itemsPerPage: data.itemsPerPage
            });

            console.log('ListResorts.useEffect(): data:', safeStringify(data, 2, ['resorts']));
            if (!firstLoad) {
                if (data.actualResortsFound > 0) {
                    notify(t('Found {{count}} resorts matching your criteria', {count: data.actualResortsFound}));
                } else {
                    notify(t('No resorts matching your criteria. Showing all resorts'));
                }
                setFirstLoad(true);
            }
            if (data.error) {
                notify(data.error.message);
            }

            window.scrollTo({ top: 0, behavior: 'smooth' });

        })
        .catch(error => {
            // Ignore cancellations caused by cleanup (unmount / re-fetch)
            if (error && error.name === 'AbortError') return;
            console.error('ListResorts.useEffect(): fetch failed:', error);
            setErrorHtml(String(error));
        })
        .finally(() => {
            if (!controller.signal.aborted) {
                setLoading(false);
            }
        });

        return () => controller.abort();
    }, [searchCriterias, currentPage]);

    const handlePageChanged = (page) => {
        console.log("handlePageChanged(): page: " + page);  
        //setPager(prev => ({...prev, current: page}));
        setCurrentPage(page);
    }
/** 
        "resort_id": 256,
        "name": "Kolonada",
        "stars": 4,
        "address": "I.P.Pavlova 8, Karlovy Vary",
        "city_name": "Karlovy Vary",
        "region_name": "Czech Republic",
        "attrs": 2910,
        "description": "Hotel Kolonada is located in Karlovy Vary. Nearby are the healing drinking thermal springs of the Garden Colonnade, Melnichnaya, Geysernaya. The hotel has 161 rooms of different categories.\r\n\r\nWatch a detailed video review here:  #v{https:\/\/youtu.be\/2xaXwkH8pfs}",
        "filename": "hotel_256.jpg",
        "resort_page": "\/component\/kurortlife\/?view=resort&id=256",
        "image_url": "\/media\/com_kurortlife\/hotel-images-cached\/hotel_256.jpg" 
*/

    return (
        <>
        <div className='resort-list-section'>
            {loading ? (
                <div className='resort-list-loading' role='status' aria-live='polite'>
                    <div className='resort-list-loading-spinner' aria-hidden='true'></div>
                    <span>{t('Loading resorts ...')}</span>
                </div>
            ) : errorHtml ? (
                <div className='resort-list-container error' dangerouslySetInnerHTML={{__html: errorHtml}} />
            ) : (
            <>
            <div className='resort-list-container'>
                
                    {resorts.map(resort => (
                        <div className='resort-list-item-card' key={resort.resort_id}>
                            <div className='resort-list-item-image-wrapper'>
                                <a href={resort.resort_page} className='resort-list-item-image-link'>
                                    <img src={resort.image_url} className='resort-list-item-image-image' />
                                </a>
                            </div>
                            <div className='resort-list-item-description-wrapper'>
                                <h2 className='resort-list-item-title-wrapper'>
                                    <a className='resort-list-item-title-link' href={resort.resort_page}>{resort.name}</a>
                                    <Stars rating={resort.stars} maxStars={5}/>
                                </h2>
                                <h5 className='resort-list-item-location'>
                                    {t('location')}: {resort.address}, {resort.region_name}
                                </h5>
                                <div className='p resort-list-item-text' dangerouslySetInnerHTML={{ __html: resort.description }} />
                                <p className='resort-list-item-price-wrapper'>
                                    <ResortPrices
                                        resortId={resort.resort_id}
                                        occupacyValue={occupacyValue}
                                        dietaryValue={dietaryValue}
                                        dateRangeValue={dateRangeValue}
                                        {...resortPriceCssClasses}
                                    />
                                </p>
                            </div>
                        </div>
                    ))}
                
            </div>
            <div className='pager-wrapper'>
                <Pager total={pager.total}
                    current={pager.current}
                    visiblePages={pager.visiblePages}
                    titles={{ first: '|<', prev:'<', next: '>', last: '>|' }}
                    className="pagination-sm pull-right"
                    onPageChanged={handlePageChanged}
                />
            </div>
            </>
            )}
        </div>
        <ToastContainer />
        </>
    );
};

export default ListResorts;
